import { useEffect, useMemo, useState } from "react";
import { Instagram, MessageCircle, Sparkles } from "lucide-react";
import { imgUrl } from "../api";
import { useSettings } from "../context/SettingsContext";
import Seo from "../components/Seo";

function remainingParts(launchAt, now) {
  const target = Date.parse(launchAt || "");
  if (!Number.isFinite(target)) return null;
  const total = Math.max(0, target - now);
  return {
    total,
    days: Math.floor(total / 86400000),
    hours: Math.floor((total / 3600000) % 24),
    minutes: Math.floor((total / 60000) % 60),
    seconds: Math.floor((total / 1000) % 60),
  };
}

function formatIst(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(iso));
}

export default function UnderConstruction({ status, loading }) {
  const { settings, whatsappLink } = useSettings();
  const [browserNow, setBrowserNow] = useState(Date.now());
  const scheduled = status?.mode === "scheduled" && Boolean(status?.scheduled_at);
  const serverOffset = useMemo(() => {
    const serverTime = Date.parse(status?.server_time || "");
    return Number.isFinite(serverTime) ? serverTime - Date.now() : 0;
  }, [status?.server_time]);
  const countdown = useMemo(
    () => remainingParts(status?.scheduled_at, browserNow + serverOffset),
    [browserNow, serverOffset, status?.scheduled_at],
  );
  const logo = settings.logo_url ? imgUrl(settings.logo_url) : "/logo.png";

  useEffect(() => {
    if (!scheduled) return undefined;
    const timer = window.setInterval(() => setBrowserNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [scheduled]);

  return (
    <main className="construction-page" data-testid="under-construction-page">
      <Seo
        title="Launching Soon"
        description="The Lalita Soujanya digital atelier is being prepared. Visit again soon for contemporary Indian designer occasion wear from Hyderabad."
        path="/"
        robots="noindex, nofollow"
      />
      <div
        className="construction-backdrop"
        style={{ backgroundImage: "url('/assets/editorial_banner.jpg')" }}
        aria-hidden="true"
      />
      <div className="construction-grain" aria-hidden="true" />

      <section className="construction-panel">
        <div className="construction-logo-wrap">
          <img src={logo} alt="Lalita Soujanya" className="construction-logo" />
        </div>
        <div className="construction-rule"><span /></div>
        <span className="construction-kicker"><Sparkles size={13} /> Hyderabad atelier</span>
        <h1>{status?.headline || "A new chapter is taking shape."}</h1>
        <p className="construction-copy">
          {status?.message || "Our digital atelier is being prepared with care. We look forward to welcoming you soon."}
        </p>

        {scheduled && countdown && (
          <div className="construction-countdown-wrap">
            <span className="construction-date">Opening {formatIst(status.scheduled_at)} IST</span>
            {countdown.total > 0 ? (
              <div className="construction-countdown">
                {[
                  [countdown.days, "Days"],
                  [countdown.hours, "Hours"],
                  [countdown.minutes, "Minutes"],
                  [countdown.seconds, "Seconds"],
                ].map(([value, label]) => (
                  <div key={label}><strong>{String(value).padStart(2, "0")}</strong><span>{label}</span></div>
                ))}
              </div>
            ) : (
              <p className="construction-finalising">The doors are opening now&hellip;</p>
            )}
          </div>
        )}

        {!scheduled && (
          <p className="construction-note">{loading ? "Checking the atelier doors…" : "Launch details will be revealed soon."}</p>
        )}

        <div className="construction-actions">
          <a href={whatsappLink("Hi Lalita Soujanya, I'd like to know when the new collection launches.")} target="_blank" rel="noreferrer">
            <MessageCircle size={16} /> WhatsApp the studio
          </a>
          <a href={`https://instagram.com/${settings.instagram_handle || "lalitasoujanya_23"}`} target="_blank" rel="noreferrer">
            <Instagram size={16} /> Follow on Instagram
          </a>
        </div>

        <div className="construction-foot">
          <span>Contemporary Indian occasionwear</span>
          <a href="/admin/login">Studio access</a>
        </div>
      </section>
    </main>
  );
}
